import { useLayoutEffect, useRef, useState } from "react";

const CHART_CHROME_HEIGHT = 72;

export function calculateShareFit({ availableRows, plotWidth, viewportHeight, fixedHeight, rowHeight, blurredRowHeight, total }: {
  availableRows: number; plotWidth: number; viewportHeight: number; fixedHeight: number;
  rowHeight: number; blurredRowHeight: number; total: number;
}) {
  const maxPlot = Math.max(150, Math.round(plotWidth * .32));
  const target = viewportHeight - 56;
  let clearRows = Math.min(2, availableRows);
  let plotHeight = 150;
  for (let count = clearRows; count >= Math.min(1, availableRows); count -= 1) {
    const room = target - fixedHeight - count * rowHeight - (total > count ? blurredRowHeight : 0);
    clearRows = count;
    plotHeight = Math.max(150, Math.min(maxPlot, room));
    if (room >= 150) break;
  }
  return { clearRows, plotHeight };
}

/** Measures the page's actual typography, Google button and gate instead of assuming a fixed gate height. */
export function useShareViewportFit(availableRows: number, total: number, enabled: boolean, layoutKey: string) {
  const sectionRef = useRef<HTMLElement>(null);
  const [fit, setFit] = useState({ clearRows: Math.min(2, availableRows), plotHeight: undefined as number | undefined });
  const [showBar, setShowBar] = useState(false);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const main = section.closest("main");
    const bar = section.querySelector<HTMLElement>("[data-share-gate-bar]");
    let frame = 0;
    const updateBar = () => {
      const button = section.querySelector<HTMLElement>('[data-gate-unlock]');
      if (!button) { setShowBar(false); return; }
      const bounds = button.getBoundingClientRect();
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const barHeight = section.querySelector<HTMLElement>("[data-share-gate-bar]")?.getBoundingClientRect().height || 76;
      // Keep this threshold stable as the bar slides away, avoiding a visibility feedback loop.
      setShowBar(enabled && !(bounds.top >= 0 && bounds.bottom <= viewportHeight - barHeight));
    };
    const measure = () => {
      // Keep space for the full bar, including safe-area padding, while it slides in and out.
      const barHeight = enabled ? bar?.getBoundingClientRect().height ?? 0 : 0;
      if (barHeight > 0) main?.style.setProperty("--share-gate-bar-height", `${Math.ceil(barHeight)}px`);
      else main?.style.removeProperty("--share-gate-bar-height");
      const surface = section.querySelector<HTMLElement>('[data-chart-visual-style="retro"]');
      const gate = section.querySelector<HTMLElement>('[data-testid="product-share-gate-page"]');
      if (!surface) {
        updateBar();
        return;
      }
      const chartWidth = surface.getBoundingClientRect().width;
      if (!chartWidth) return;
      if (window.innerWidth < 640) {
        setFit({ clearRows: Math.min(2, availableRows), plotHeight: chartWidth - CHART_CHROME_HEIGHT });
      } else if (enabled && gate) {
        const rows = section.querySelectorAll<HTMLElement>('[data-teaser-row]');
        const blurred = section.querySelector<HTMLElement>('[data-blurred-row]');
        const rowHeight = rows[0]?.getBoundingClientRect().height ?? 48;
        const blurredRowHeight = blurred?.getBoundingClientRect().height ?? 48;
        const currentPlot = surface.getBoundingClientRect().height - CHART_CHROME_HEIGHT;
        const bottom = gate.getBoundingClientRect().bottom + window.scrollY;
        const next = calculateShareFit({
          availableRows, total, plotWidth: chartWidth - 72, viewportHeight: window.innerHeight,
          fixedHeight: bottom - currentPlot - rows.length * rowHeight - (blurred ? blurredRowHeight : 0),
          rowHeight, blurredRowHeight,
        });
        // Keep the chart's default 16:9 ratio on desktop; only the disclosed row count adapts.
        setFit(previous => previous.clearRows === next.clearRows && previous.plotHeight === undefined
          ? previous : { clearRows: next.clearRows, plotHeight: undefined });
      } else {
        setFit(previous => previous.plotHeight === undefined ? previous : { ...previous, plotHeight: undefined });
      }
      updateBar();
    };
    const scheduleMeasure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(scheduleMeasure);
    observer?.observe(section);
    if (bar) observer?.observe(bar, { box: "border-box" });
    measure();
    window.addEventListener("resize", scheduleMeasure);
    window.addEventListener("scroll", updateBar, { passive: true });
    window.visualViewport?.addEventListener("resize", scheduleMeasure);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      main?.style.removeProperty("--share-gate-bar-height");
      window.removeEventListener("resize", scheduleMeasure);
      window.removeEventListener("scroll", updateBar);
      window.visualViewport?.removeEventListener("resize", scheduleMeasure);
    };
  }, [availableRows, enabled, layoutKey, total]);

  return { sectionRef, ...fit, showBar };
}
